import { Controller } from "@hotwired/stimulus"

// Debounced background save of a form, without submitting it.
//
// Deliberately NOT the shared auto-submit controller: that one calls
// requestSubmit() on the real form, which here would run the wizard's advancing
// action, and would also re-trigger Active Storage's direct upload on every
// keystroke. This posts a copy of the fields to a dedicated endpoint that only
// ever answers a status code, so the page the user is typing into is untouched.
//
// Usage:
//   form data-controller="autosave"
//        data-autosave-url-value="/..."
//        data-action="input->autosave#schedule change->autosave#saveNow
//                     turbo:before-visit@document->autosave#flush
//                     beforeunload@window->autosave#flush"
export default class extends Controller {
  static targets = ["status"]
  static values = {
    url: String,
    delay: { type: Number, default: 1200 },
    savingText: String,
    savedText: String,
    errorText: String,
  }

  connect() {
    this.dirty = false
  }

  disconnect() {
    clearTimeout(this.timeout)
  }

  // Typing: wait until they pause, so one save covers a whole field.
  schedule() {
    this.dirty = true
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.save(), this.delayValue)
  }

  // Leaving a field or picking from a select — the value is settled, save it now.
  saveNow() {
    this.dirty = true
    clearTimeout(this.timeout)
    this.save()
  }

  // Navigating away (the handbook link on this step, or closing the tab) with a
  // debounce still pending. Without this the last thing typed is exactly what
  // gets lost — which is the whole reason this controller exists.
  flush() {
    if (!this.dirty) return

    clearTimeout(this.timeout)
    this.save()
  }

  save() {
    if (!this.dirty || !this.urlValue) return

    // Cleared before the request, not after: anything typed while it is in
    // flight has to count as a new change, or that edit is dropped.
    this.dirty = false
    this.setStatus(this.savingTextValue)

    // keepalive lets the request outlive the page — a fetch started during
    // turbo:before-visit or beforeunload is otherwise cancelled on navigation.
    fetch(this.urlValue, {
      method: "PATCH",
      body: this.payload(),
      headers: { "X-CSRF-Token": this.csrfToken, Accept: "application/json" },
      credentials: "same-origin",
      keepalive: true,
    })
      .then((response) => {
        this.setStatus(response.ok ? this.savedTextValue : this.errorTextValue)
      })
      .catch(() => {
        // A failed background save must never surface as a broken page. The
        // status says so, and the explicit submit is still there.
        this.dirty = true
        this.setStatus(this.errorTextValue)
      })
  }

  // File inputs are stripped: re-sending the logo on every keystroke would
  // re-upload it, and an empty file field would read as "remove it". The server
  // refuses attachments on this endpoint too — this is the client half of that.
  payload() {
    const data = new FormData(this.element)

    this.element.querySelectorAll('input[type="file"]').forEach((input) => {
      if (input.name) data.delete(input.name)
    })

    return data
  }

  setStatus(text) {
    if (!this.hasStatusTarget || !text) return

    this.statusTarget.textContent = text
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
